import { isEmpty } from "ramda";
import {
  EPersonType,
  EProposalFileType,
  GetProposalAndIndicationByIdQuery,
  ContractCustomerRefPendenciesModel,
  ContractCustomerRefLegalRepresentativePendenciesModel,
  EContractStatus,
} from "~/graphql/types";
import { Unarray } from "~/utils";
import type {
  DocumentationAnalysisFileModel,
  CustomerGetQuery,
} from "~/graphql/types";
import {
  ContractByIndicationFlow,
  CustomerByIndicationFlow,
  IndicationByIndicationFlow,
} from "~/contexts/indicationFlow/types";

export type AddressForm = {
  city: string;
  state: string;
  street: string;
  number: string;
  zipcode: string;
  complement: string;
  neighborhood: string;
};

export type LegalRepresentativeType = {
  //strings
  name: string;
  email: string;
  phone: string;
  document: string;
  address: AddressForm;
  identificationNumber: string;
  pendencies?:
    | ContractCustomerRefLegalRepresentativePendenciesModel
    | null
    | undefined;
  files?: (
    | DocumentationAnalysisFileModel
    | Unarray<
        NonNullable<
          NonNullable<CustomerGetQuery["customerGet"]>["legalRepresentatives"]
        >[number]["files"]
      >
  )[];

  //files
  customerPjRepresentativeDocumentWithPhotoBack: null | File[];
  customerPjRepresentativeDocumentWithPhotoFront: null | File[];
};

export const LEGAL_REPRESENTATIVE_INITIAL_VALUES: LegalRepresentativeType = {
  name: "",
  email: "",
  phone: "",
  document: "",
  identificationNumber: "",
  address: {
    city: "",
    state: "",
    number: "",
    street: "",
    zipcode: "",
    complement: "",
    neighborhood: "",
  },
  customerPjRepresentativeDocumentWithPhotoBack: null,
  customerPjRepresentativeDocumentWithPhotoFront: null,
};

// type PendencyObject = NonNullable<GetProposalAndIndicationByIdQuery['getIndicationRelatedEntitiesById']['proposal']>['customerRef']['pendencies']
// Contract pendency object (for ContractFlow)
type ContractPendencyObject = NonNullable<
  GetProposalAndIndicationByIdQuery["getIndicationRelatedEntitiesById"]["contract"]
>["customerRef"]["pendencies"];

export type BasicDataPendency = "name" | "document" | "email" | "phone";

// Disable input when either there's no pendency tracking, not sent for analysis, or not rejected
export const hasPendencyOrNullOnBasicData = (
  fieldName: BasicDataPendency,
  pendencies: ContractCustomerRefPendenciesModel | undefined | null,
) => {
  // Map mismatch between UI field and backend pendency key
  if (!pendencies) {
    return true;
  }

  const pendency =
    pendencies[fieldName as keyof ContractCustomerRefPendenciesModel];

  // If the field was rejected, enable editing (not disabled)
  if (pendency?.rejectedAt) {
    return false;
  }

  // If it wasn't sent for analysis, keep disabled
  if (!pendency?.sentAt) {
    return true;
  }

  // If sent and not rejected, keep disabled
  return true;
};

// Contract uses 'zipCode' instead of 'zipcode'
export type AddressPendency =
  | "zipCode"
  | "state"
  | "city"
  | "neighborhood"
  | "street"
  | "number";

export const hasPendencyOrNullOnAddress = (
  fieldName: AddressPendency,
  pendencies: ContractCustomerRefPendenciesModel | undefined | null,
) => {
  if (!pendencies) {
    return true;
  }

  if (
    !pendencies[fieldName as keyof ContractCustomerRefPendenciesModel]?.sentAt
  ) {
    return false;
  }

  if (
    pendencies[fieldName as keyof ContractCustomerRefPendenciesModel]
      ?.rejectedAt
  ) {
    return false;
  }

  return true;
};

export type LegalRepresentativyPendency =
  keyof ContractCustomerRefLegalRepresentativePendenciesModel;

export const hasPendencyOrNullOnLegalRepresentativy = (
  fieldName: LegalRepresentativyPendency,
  pendencies?:
    | ContractCustomerRefLegalRepresentativePendenciesModel
    | null
    | undefined,
) => {
  // Desabilita quando não há pendência, não foi enviado para análise ou não foi rejeitado.
  // Habilita apenas quando o campo foi rejeitado.
  if (!pendencies) {
    return true;
  }

  const pendency = pendencies[fieldName];

  // Se rejeitado, habilita edição (não desabilita)
  if (pendency?.rejectedAt) {
    return false;
  }

  // Se não foi enviado para análise, mantém desabilitado
  if (!pendency?.sentAt) {
    return true;
  }

  // Se enviado e não rejeitado, mantém desabilitado
  return true;
};

export const hasDuplicatedValuesInLegalRepresentativies = (array: any[]) => {
  const identificationNumber = array.map(function (value) {
    return value.identificationNumber;
  });

  const document = array.map(function (value) {
    return value.document;
  });

  const phone = array.map(function (value) {
    return value.phone;
  });

  const email = array.map(function (value) {
    return value.email;
  });

  const myValues = [...identificationNumber, ...document, ...phone, ...email];

  return myValues.some(function (value, index, myArray) {
    if (value?.length > 1) {
      return myArray.indexOf(value) !== myArray.lastIndexOf(value);
    }
  });
};

type FileContract = Unarray<
  NonNullable<
    GetProposalAndIndicationByIdQuery["getIndicationRelatedEntitiesById"]["contract"]
  >["files"]
>;

export const documentFileIsDisabled = (file: FileContract | undefined) => {
  if (!file?.sentAt) {
    return false;
  }

  if (file?.rejectedAt) {
    return false;
  }

  return true;
};

export const shouldDisableStep = (
  label: string,
  contract: ContractByIndicationFlow | null,
) => {
  if (!contract) {
    return label !== "Dados básicos";
  }

  if (label === "Assinatura do cliente") {
    const termOfAdhesion = contract?.termOfAdhesion?.find(
      (term) => term.active,
    );
    const termOfManagement = contract?.termOfManagement?.find(
      (term) => term.active,
    );

    return Boolean(
      termOfAdhesion &&
      termOfAdhesion.signedAt &&
      termOfManagement &&
      termOfManagement.signedAt,
    );
  }

  return false;
};

export const isStepCompleted = (
  label: string,
  contract: ContractByIndicationFlow | null,
  customer: CustomerByIndicationFlow | null,
) => {
  if (!contract) {
    return false;
  }

  if (contract.status === EContractStatus.active) {
    return true;
  }

  const {
    customerRef,
    files,
    energyBillInfo,
    termOfAdhesion,
    termOfManagement,
  } = contract;
  const {
    name,
    personType,
    legalRepresentatives,
    email,
    phone,
    document,
    pendencies,
  } = customerRef;

  const isPf = personType === EPersonType.pf;

  const basicDataHasPendency = Boolean(
    pendencies?.name?.rejectedAt ||
    pendencies?.email?.rejectedAt ||
    pendencies?.phone?.rejectedAt ||
    (pendencies as any)?.uf?.rejectedAt,
  );

  const addressHasPendency = Boolean(
    pendencies?.zipCode?.rejectedAt ||
    pendencies?.state?.rejectedAt ||
    pendencies?.city?.rejectedAt ||
    pendencies?.neighborhood?.rejectedAt ||
    pendencies?.street?.rejectedAt ||
    pendencies?.number?.rejectedAt,
  );

  const legalRepresentativesHasPendency = Boolean(
    customerRef?.legalRepresentatives?.some((rep) =>
      Boolean(
        rep.pendencies?.name?.rejectedAt ||
        rep.pendencies?.document?.rejectedAt ||
        rep.pendencies?.email?.rejectedAt ||
        rep.pendencies?.phone?.rejectedAt ||
        rep.pendencies?.zipCode?.rejectedAt ||
        rep.pendencies?.state?.rejectedAt ||
        rep.pendencies?.city?.rejectedAt ||
        rep.pendencies?.neighborhood?.rejectedAt ||
        rep.pendencies?.number?.rejectedAt ||
        rep.files?.some((f) => f.rejectedAt),
      ),
    ),
  );

  const documentsHasPendency = Boolean(files?.some((item) => item.rejectedAt));

  if (label === "Dados básicos") {
    const baseValidation = Boolean(name && document && email && phone);
    return isPf
      ? Boolean(baseValidation && !basicDataHasPendency)
      : Boolean(baseValidation && !basicDataHasPendency);
  }

  if (label === "Endereço") {
    const addressFilled = Boolean(
      customer?.address?.zipcode &&
      customer?.address?.street &&
      customer?.address?.neighborhood &&
      customer?.address?.city &&
      customer?.address?.state &&
      customer?.address?.number,
    );
    return Boolean(addressFilled && !addressHasPendency);
  }

  if (label === "Representantes") {
    return Boolean(
      !isEmpty(legalRepresentatives) && !legalRepresentativesHasPendency,
    );
  }

  if (label === "Documentos") {
    const hasEnergyBillDocument = Boolean(
      files?.find((item) => item.type === EProposalFileType.energyBill)?.sentAt,
    );
    const hasEnegyBillInfo = Boolean(
      energyBillInfo?.tension &&
      energyBillInfo?.AmountCents &&
      energyBillInfo?.distributionTaxes &&
      energyBillInfo?.clientNumber &&
      energyBillInfo?.installationNumber &&
      energyBillInfo?.tariffClassification &&
      energyBillInfo?.address,
    );

    if (isPf) {
      const hasFrontCustomerDocument = Boolean(
        files?.find(
          (item) =>
            item.type === EProposalFileType.customerPfDocumentWithPhotoFront,
        )?.sentAt,
      );
      const hasBackCustomerDocument = Boolean(
        files?.find(
          (item) =>
            item.type === EProposalFileType.customerPfDocumentWithPhotoBack,
        )?.sentAt,
      );
      return Boolean(
        hasEnegyBillInfo &&
        hasFrontCustomerDocument &&
        hasBackCustomerDocument &&
        hasEnergyBillDocument &&
        !documentsHasPendency,
      );
    }

    const hasCustomerPjCardDocument = Boolean(
      files?.find(
        (item) => item.type === EProposalFileType.customerPjDocumentCard,
      )?.sentAt,
    );
    const hasSocialContractDocument = Boolean(
      files?.find(
        (item) => item.type === EProposalFileType.customerPjSocialContract,
      )?.sentAt,
    );
    return Boolean(
      hasCustomerPjCardDocument &&
      hasSocialContractDocument &&
      hasEnegyBillInfo &&
      hasEnergyBillDocument &&
      !documentsHasPendency,
    );
  }

  if (label === "Assinatura do cliente") {
    const activeAdhesion = termOfAdhesion?.find((t) => t.active);
    const activeManagement = termOfManagement?.find((t) => t.active);
    return Boolean(activeAdhesion?.signedAt && activeManagement?.signedAt);
  }

  return false;
};
